export type OrgType =
  | 'vc_fund'
  | 'ecosystem_convener'
  | 'corporate_industry'
  | 'research_institution'
  | 'government_agency'
  | 'accelerator_incubator'
  | 'inventor_personal'

export interface OrgVocabulary {
  collectionFallbackName: string
  collectionLabel: string
  entitySingular: string
  entityPlural: string
  activeSectionLabel: string
  loadFailedMessage: string
  inboundTitle: string
  inboundSubtitle: string
  inviteDialogTitle: string
  conflictLabel: string
}

export const ORG_VOCABULARY: Record<OrgType, OrgVocabulary> = {
  vc_fund: {
    collectionFallbackName: 'My Portfolio',
    collectionLabel: 'portfolio',
    entitySingular: 'entity',
    entityPlural: 'entities',
    activeSectionLabel: 'Active Disclosures',
    loadFailedMessage: 'Failed to load portfolio',
    inboundTitle: "Today's inbound, scored",
    inboundSubtitle: 'Matched blind against your whole book — ranked by match strength',
    inviteDialogTitle: 'Invite entities to portfolio',
    conflictLabel: 'already in portfolio',
  },
  ecosystem_convener: {
    collectionFallbackName: 'My Ecosystem',
    collectionLabel: 'member',
    entitySingular: 'organisation',
    entityPlural: 'organisations',
    activeSectionLabel: 'Active Connections',
    loadFailedMessage: 'Failed to load ecosystem',
    inboundTitle: "Today's use-case matches, scored",
    inboundSubtitle: 'Matched blind across the ecosystem — ranked by connection strength',
    inviteDialogTitle: 'Invite organisations to ecosystem',
    conflictLabel: 'already a member',
  },
  corporate_industry: {
    collectionFallbackName: 'My Network',
    collectionLabel: 'innovation',
    entitySingular: 'partner',
    entityPlural: 'partners',
    activeSectionLabel: 'Active Engagements',
    loadFailedMessage: 'Failed to load network',
    inboundTitle: "Today's inbound opportunities, scored",
    inboundSubtitle: 'Matched blind against your innovation priorities — ranked by fit',
    inviteDialogTitle: 'Invite partners to network',
    conflictLabel: 'already a partner',
  },
  research_institution: {
    collectionFallbackName: 'My Portfolio',
    collectionLabel: 'research',
    entitySingular: 'asset',
    entityPlural: 'assets',
    activeSectionLabel: 'Active Collaborations',
    loadFailedMessage: 'Failed to load portfolio',
    inboundTitle: "Today's collaboration matches, scored",
    inboundSubtitle: 'Matched blind across research domains — ranked by match strength',
    inviteDialogTitle: 'Invite partners to research portfolio',
    conflictLabel: 'already in portfolio',
  },
  government_agency: {
    collectionFallbackName: 'My Network',
    collectionLabel: 'initiative',
    entitySingular: 'partner',
    entityPlural: 'partners',
    activeSectionLabel: 'Active Partnerships',
    loadFailedMessage: 'Failed to load network',
    inboundTitle: "Today's capability matches, scored",
    inboundSubtitle: 'Matched blind against policy priorities — ranked by alignment',
    inviteDialogTitle: 'Invite partners to initiative',
    conflictLabel: 'already a partner',
  },
  accelerator_incubator: {
    collectionFallbackName: 'My Cohort',
    collectionLabel: 'cohort',
    entitySingular: 'company',
    entityPlural: 'companies',
    activeSectionLabel: 'Active Connections',
    loadFailedMessage: 'Failed to load cohort',
    inboundTitle: "Today's cohort matches, scored",
    inboundSubtitle: 'Matched blind across your cohort — ranked by connection potential',
    inviteDialogTitle: 'Invite companies to cohort',
    conflictLabel: 'already in cohort',
  },
  inventor_personal: {
    collectionFallbackName: 'My Workspace',
    collectionLabel: 'workspace',
    entitySingular: 'project',
    entityPlural: 'projects',
    activeSectionLabel: 'Active Disclosures',
    loadFailedMessage: 'Failed to load workspace',
    inboundTitle: "Today's matches, scored",
    inboundSubtitle: 'Matched blind against your disclosures — ranked by match strength',
    inviteDialogTitle: 'Invite collaborators',
    conflictLabel: 'already added',
  },
}

export const DEFAULT_ORG_TYPE: OrgType = 'vc_fund'

export function getOrgVocabulary(orgType: string | null | undefined): OrgVocabulary {
  return ORG_VOCABULARY[(orgType as OrgType) ?? DEFAULT_ORG_TYPE] ?? ORG_VOCABULARY[DEFAULT_ORG_TYPE]
}
